import React, { useState } from 'react';
import { toast } from '../utils/toast';
import { geminiApi } from '../services/geminiApi';
import { QuickStart } from '../components/QuickStart';
import { BetaBadge } from '../components/BetaDisclaimer';
import { VoiceRecorder } from '../components/VoiceRecorder';
import pptxgenjs from 'pptxgenjs';
import { jsPDF } from 'jspdf';
import DeploymentOptions from '../components/DeploymentOptions';

export function PowerPointGenerator() {
  const [slideContent, setSlideContent] = useState('');
  const [selectedStyle, setSelectedStyle] = useState('professional');
  const [selectedLayout, setSelectedLayout] = useState('modern');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedSlides, setGeneratedSlides] = useState([]);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [currentBatch, setCurrentBatch] = useState(0);
  const [totalBatches, setTotalBatches] = useState(0);

  const styleOptions = [
    { value: 'professional', label: 'Professional', description: 'Clean, corporate style with emphasis on clarity' },
    { value: 'creative', label: 'Creative', description: 'Dynamic layouts with artistic elements' },
    { value: 'minimal', label: 'Minimal', description: 'Simple, focused design with essential elements' },
    { value: 'bold', label: 'Bold', description: 'High-impact design with strong visuals' }
  ];

  const layoutOptions = [
    { value: 'modern', label: 'Modern', description: 'Contemporary layout with clean lines' },
    { value: 'classic', label: 'Classic', description: 'Traditional presentation layout' },
    { value: 'grid', label: 'Grid', description: 'Structured layout with grid system' },
    { value: 'dynamic', label: 'Dynamic', description: 'Varied layouts for engaging content' }
  ];

  const quickStartGuides = [
    {
      title: "Content Structure",
      description: "Use [Title] for slide titles and [Content] for main points. Separate slides with ---",
      keywords: ["Format", "Structure", "Organization"],
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
        </svg>
      )
    },
    {
      title: "Design Selection",
      description: "Choose a style and layout that matches your content and audience",
      keywords: ["Style", "Layout", "Design"],
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
        </svg>
      )
    },
    {
      title: "Preview & Export",
      description: "Review your slides in the preview panel and export to PowerPoint or PDF",
      keywords: ["Preview", "Export", "Download"],
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
        </svg>
      )
    }
  ];

  const generateSlides = async () => {
    if (!slideContent.trim()) {
      toast.error('Please enter content for your slides');
      return;
    }

    setIsGenerating(true);
    setGeneratedSlides([]);
    setGenerationProgress(0);
    setCurrentBatch(0);

    try {
      // Process the content into slides
      const slides = slideContent
        .split('---')
        .map(slide => slide.trim())
        .filter(Boolean)
        .map(slide => {
          const titleMatch = slide.match(/\[Title\](.*?)(?:\[Content\]|$)/is);
          const contentMatch = slide.match(/\[Content\](.*?)$/is);
          
          return {
            title: titleMatch ? titleMatch[1].trim() : '',
            content: contentMatch ? contentMatch[1].trim() : slide.trim()
          };
        });

      // Use smaller batch size for better reliability
      const BATCH_SIZE = 2;
      const batches = Math.ceil(slides.length / BATCH_SIZE);
      setTotalBatches(batches);
      let allGeneratedSlides = [];

      // Process slides in batches
      for (let batchIndex = 0; batchIndex < batches; batchIndex++) {
        setCurrentBatch(batchIndex + 1);
        const batchStart = batchIndex * BATCH_SIZE;
        const batchSlides = slides.slice(batchStart, batchStart + BATCH_SIZE);
        
        // Function to generate a single batch with retries
        const generateBatchWithRetry = async (retryCount = 0) => {
          try {
            // Simplified prompt for better reliability
            const contextPrompt = `Create ${batchSlides.length} presentation slides with the following specifications:
Style: ${selectedStyle}
Layout: ${selectedLayout}

${batchIndex > 0 ? `Previous slide title: "${allGeneratedSlides[allGeneratedSlides.length - 1]?.title}"` : ''}

For each slide below, create:
- A clear title
- 2-4 concise bullet points
- Brief speaker notes
- A suggested visual/graphic

Input slides:
${batchSlides.map((slide, i) => `
Slide ${batchStart + i + 1}:
${slide.title ? `Title: ${slide.title}` : 'Title: [Generate appropriate title]'}
Content: ${slide.content}
`).join('\n')}

Return a JSON array with this structure for each slide:
{
  "title": "string",
  "content": ["2-4 bullet points"],
  "design": {
    "layout": "${selectedLayout}",
    "graphics": ["suggested visual"]
  },
  "notes": "brief notes"
}`;

            const response = await geminiApi.generateText(contextPrompt);
            
            if (response.error) {
              throw new Error(response.error);
            }

            // Parse the generated content
            let batchContent;
            try {
              // Clean up the response to handle potential formatting issues
              const cleanedResponse = response.message.replace(/```json\n?|\n?```/g, '').trim();
              const jsonMatch = cleanedResponse.match(/\[[\s\S]*\]/);
              
              if (jsonMatch) {
                batchContent = JSON.parse(jsonMatch[0]);
              } else {
                throw new Error('No valid JSON found in response');
              }

              // Validate the response structure
              if (!Array.isArray(batchContent) || batchContent.length === 0) {
                throw new Error('Invalid response structure');
              }

              // Ensure each slide has the required properties
              batchContent = batchContent.map((slide, index) => ({
                title: slide.title || `Slide ${batchStart + index + 1}`,
                content: Array.isArray(slide.content) ? slide.content : [slide.content],
                design: {
                  layout: selectedLayout,
                  graphics: slide.design?.graphics || ['Relevant visual'],
                  color: selectedStyle === 'professional' ? 'blue' : 'dynamic'
                },
                notes: slide.notes || 'Present the key points with confidence'
              }));
              
              return batchContent;
            } catch (e) {
              console.error('Error parsing response:', e);
              throw new Error('Failed to parse API response');
            }
          } catch (error) {
            if (retryCount < 3) {
              // Wait before retrying (exponential backoff)
              await new Promise(resolve => setTimeout(resolve, 1000 * Math.pow(2, retryCount)));
              return generateBatchWithRetry(retryCount + 1);
            }
            throw error;
          }
        };

        try {
          // Generate batch with retry logic
          const batchContent = await generateBatchWithRetry();
          
          // Add batch to all slides
          allGeneratedSlides = [...allGeneratedSlides, ...batchContent];
          
          // Update progress
          const progress = Math.round((batchIndex + 1) / batches * 100);
          setGenerationProgress(progress);
          setGeneratedSlides(allGeneratedSlides);
          setCurrentSlideIndex(allGeneratedSlides.length - batchContent.length);
          
          // Show progress toast
          toast.info(`Generated ${allGeneratedSlides.length} of ${slides.length} slides`);
          
          // Add delay between batches
          if (batchIndex < batches - 1) {
            await new Promise(resolve => setTimeout(resolve, 3000));
          }
        } catch (batchError) {
          console.error(`Error generating batch ${batchIndex + 1}:`, batchError);
          
          // Create basic slides for this batch
          const fallbackContent = batchSlides.map((slide, index) => ({
            title: slide.title || `Slide ${batchStart + index + 1}`,
            content: slide.content.split('\n')
              .filter(line => line.trim())
              .map(line => line.trim().replace(/^[-*]\s*/, '')),
            design: {
              layout: selectedLayout,
              graphics: ['Relevant visual'],
              color: selectedStyle === 'professional' ? 'blue' : 'dynamic'
            },
            notes: 'Present the key points clearly'
          }));
          
          allGeneratedSlides = [...allGeneratedSlides, ...fallbackContent];
          setGeneratedSlides(allGeneratedSlides);
          
          // Show warning but continue processing
          toast.warning(`Simplified content generated for slides ${batchStart + 1}-${batchStart + batchSlides.length}`);
        }
      }

      toast.success(`Successfully generated ${allGeneratedSlides.length} slides!`);
    } catch (error) {
      console.error('Error generating slides:', error);
      toast.error('Failed to generate slides. Please try again with fewer slides or simpler content.');
    } finally {
      setIsGenerating(false);
      setGenerationProgress(0);
      setCurrentBatch(0);
    }
  };

  const exportToPowerPoint = async () => {
    if (!generatedSlides.length) {
      toast.error('Please generate slides first');
      return;
    }

    try {
      toast.info('Generating PowerPoint file...');
      
      // Create a new presentation
      const pptx = new pptxgenjs();

      // Set theme based on style
      const themeColors = {
        professional: { 
          background: 'FFFFFF',
          accent: '2563EB',
          text: '111827'
        },
        creative: {
          background: 'FFFFFF',
          accent: '8B5CF6',
          text: '1F2937'
        },
        minimal: {
          background: 'FFFFFF',
          accent: '6B7280',
          text: '111827'
        },
        bold: {
          background: 'FFFFFF',
          accent: 'EF4444',
          text: '111827'
        }
      };

      const theme = themeColors[selectedStyle] || themeColors.professional;

      // Generate slides
      for (const slideData of generatedSlides) {
        const slide = pptx.addSlide();

        // Add title
        if (slideData.title) {
          slide.addText(slideData.title, {
            x: '5%',
            y: '5%',
            w: '90%',
            h: '15%',
            fontSize: 36,
            color: theme.text,
            bold: true,
            align: 'left'
          });
        }

        // Ensure content is an array
        const contentArray = Array.isArray(slideData.content) 
          ? slideData.content 
          : [slideData.content].filter(Boolean);

        // Add content
        const contentY = selectedLayout === 'grid' ? '25%' : '30%';

        if (selectedLayout === 'grid' && contentArray.length > 1) {
          // Grid layout: organize content in a 2x2 grid
          const gridItems = contentArray.slice(0, 4); // Limit to 4 items for 2x2 grid
          const positions = [
            { x: '5%', y: '25%' },
            { x: '52%', y: '25%' },
            { x: '5%', y: '55%' },
            { x: '52%', y: '55%' }
          ];

          gridItems.forEach((item, idx) => {
            if (item) {
              slide.addText(item.toString(), {
                ...positions[idx],
                w: '43%',
                h: '25%',
                fontSize: 18,
                color: theme.text,
                bullet: true
              });
            }
          });
        } else {
          // Standard layout: list all content vertically
          contentArray.forEach((item, idx) => {
            if (item) {
              slide.addText(item.toString(), {
                x: '5%',
                y: `${parseInt(contentY) + (idx * 10)}%`,
                w: '90%',
                h: '10%',
                fontSize: 18,
                color: theme.text,
                bullet: true
              });
            }
          });
        }

        // Add design elements if specified
        if (slideData.design) {
          if (slideData.design.graphics) {
            // Add placeholder for graphics
            slide.addShape(pptx.ShapeType.rect, {
              x: '70%',
              y: '70%',
              w: '25%',
              h: '25%',
              fill: { color: theme.accent, transparency: 50 }
            });
          }
        }

        // Add notes if present
        if (slideData.notes) {
          slide.addNotes(slideData.notes.toString());
        }
      }

      // Generate and download the file
      const filename = `presentation-${new Date().toISOString().split('T')[0]}.pptx`;
      await pptx.writeFile({ fileName: filename });
      toast.success('PowerPoint file downloaded successfully!');
    } catch (error) {
      console.error('Error exporting to PowerPoint:', error);
      toast.error(error.message || 'Failed to export PowerPoint file');
    }
  };

  const exportToPDF = async () => {
    if (!generatedSlides.length) {
      toast.error('Please generate slides first');
      return;
    }

    try {
      toast.info('Generating PDF file...');
      
      // Create a new PDF document in landscape mode (A4)
      const doc = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4'
      });

      // Set theme based on style
      const themeColors = {
        professional: {
          accent: '#2563EB',
          text: '#111827',
          subtext: '#4B5563'
        },
        creative: {
          accent: '#8B5CF6',
          text: '#1F2937',
          subtext: '#4B5563'
        },
        minimal: {
          accent: '#6B7280',
          text: '#111827',
          subtext: '#4B5563'
        },
        bold: {
          accent: '#EF4444',
          text: '#111827',
          subtext: '#4B5563'
        }
      };

      const theme = themeColors[selectedStyle] || themeColors.professional;

      // Generate slides
      generatedSlides.forEach((slideData, index) => {
        // Add a new page for each slide except the first one
        if (index > 0) {
          doc.addPage();
        }

        // Add slide number
        doc.setFontSize(10);
        doc.setTextColor(theme.subtext);
        doc.text(`${index + 1}/${generatedSlides.length}`, 280, 190, { align: 'right' });

        // Add title
        if (slideData.title) {
          doc.setFontSize(24);
          doc.setTextColor(theme.text);
          doc.text(slideData.title, 20, 20);
        }

        // Ensure content is an array
        const contentArray = Array.isArray(slideData.content) 
          ? slideData.content 
          : [slideData.content].filter(Boolean);

        // Add content based on layout
        if (selectedLayout === 'grid' && contentArray.length > 1) {
          // Grid layout: organize content in a 2x2 grid
          const gridItems = contentArray.slice(0, 4);
          const positions = [
            { x: 20, y: 50 },
            { x: 150, y: 50 },
            { x: 20, y: 100 },
            { x: 150, y: 100 }
          ];

          doc.setFontSize(12);
          doc.setTextColor(theme.text);

          gridItems.forEach((item, idx) => {
            if (item) {
              doc.text(`• ${item.toString()}`, positions[idx].x, positions[idx].y, {
                maxWidth: 120
              });
            }
          });
        } else {
          // Standard layout: list all content vertically
          doc.setFontSize(12);
          doc.setTextColor(theme.text);

          contentArray.forEach((item, idx) => {
            if (item) {
              doc.text(`• ${item.toString()}`, 20, 50 + (idx * 10), {
                maxWidth: 250
              });
            }
          });
        }

        // Add notes if present (in smaller text at the bottom)
        if (slideData.notes) {
          doc.setFontSize(8);
          doc.setTextColor(theme.subtext);
          doc.text(slideData.notes.toString(), 20, 180, {
            maxWidth: 250
          });
        }
      });

      // Save the PDF
      doc.save(`presentation-${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success('PDF file downloaded successfully!');
    } catch (error) {
      console.error('Error exporting to PDF:', error);
      toast.error(error.message || 'Failed to export PDF file');
    }
  };

  const handleVoiceTranscriptionComplete = (transcribedText) => {
    setSlideContent(prevContent => {
      // If there's existing content, add a slide separator
      const separator = prevContent ? '\n---\n' : '';
      return prevContent + separator + transcribedText;
    });
    toast.success('Voice input added successfully!');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-white to-white relative">
      {/* Background effects */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_120%,rgba(120,119,198,0.1),rgba(255,255,255,0)_43%),radial-gradient(circle_at_-20%_-20%,rgba(167,243,208,0.08),rgba(255,255,255,0)_35%)] pointer-events-none"></div>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(147,197,253,0.08),rgba(255,255,255,0)_50%)] pointer-events-none"></div>
      <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.05)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.05)_1px,transparent_1px)] bg-[size:14px_14px] pointer-events-none"></div>
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Modern SaaS Header */}
        <div className="relative mb-8 p-8 rounded-3xl bg-gradient-to-r from-primary-900 via-primary-800 to-primary-900 shadow-2xl overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff0a_1px,transparent_1px),linear-gradient(to_bottom,#ffffff0a_1px,transparent_1px)] bg-[size:24px_24px]"></div>
            <div className="absolute left-0 right-0 top-0 h-px bg-gradient-to-r from-transparent via-primary-400/20 to-transparent"></div>
            <div className="absolute left-0 right-0 bottom-0 h-px bg-gradient-to-r from-transparent via-primary-400/20 to-transparent"></div>
            <div className="absolute -left-48 -top-48 h-96 w-96">
              <div className="absolute inset-0 rotate-45 translate-x-1/2 translate-y-1/2 bg-gradient-conic from-primary-500/40 via-primary-300/10 to-primary-400/40 blur-xl opacity-30"></div>
            </div>
            <div className="absolute -right-48 -bottom-48 h-96 w-96">
              <div className="absolute inset-0 rotate-45 translate-x-1/2 translate-y-1/2 bg-gradient-conic from-primary-400/40 via-primary-300/10 to-primary-500/40 blur-xl opacity-30"></div>
            </div>
          </div>

          <div className="relative">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
              <div className="space-y-4">
                <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-primary-800/50 border border-primary-700/50 backdrop-blur-sm">
                  <div className="w-2 h-2 rounded-full bg-primary-400 animate-pulse"></div>
                  <span className="text-sm text-primary-100">AI-Powered Presentations</span>
                </div>
                <h1 className="text-4xl md:text-5xl font-bold text-white flex items-center">
                  Slide Generator
                  <BetaBadge className="ml-3" />
                </h1>
                <p className="text-lg text-primary-200 max-w-xl">
                  Create professional presentations with AI assistance
                </p>
                <div className="flex items-center gap-4 pt-2">
                  <div className="flex items-center space-x-2 text-sm text-primary-200">
                    <svg className="w-5 h-5 text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Smart Design</span>
                  </div>
                  <div className="flex items-center space-x-2 text-sm text-primary-200">
                    <svg className="w-5 h-5 text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Multiple Formats</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Input Section */}
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100">
              <div className="p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Content</h2>
                <div className="space-y-4">
                  {/* Voice Recorder */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Record Slide Content
                    </label>
                    <VoiceRecorder 
                      onTranscriptionComplete={handleVoiceTranscriptionComplete}
                      placeholder="Click to start recording your slide content..."
                      className="mb-4"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Slide Content
                    </label>
                    <textarea
                      value={slideContent}
                      onChange={(e) => setSlideContent(e.target.value)}
                      rows="10"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="Enter your slide content here...
Example:
[Title] Introduction
[Content] - Key points about the topic
- Important statistics
- Main objectives

---

[Title] Market Analysis
[Content] - Current market size
- Growth projections
- Key competitors"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Style
                      </label>
                      <select
                        value={selectedStyle}
                        onChange={(e) => setSelectedStyle(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      >
                        {styleOptions.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Layout
                      </label>
                      <select
                        value={selectedLayout}
                        onChange={(e) => setSelectedLayout(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      >
                        {layoutOptions.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <button
                    onClick={generateSlides}
                    disabled={isGenerating}
                    className={`w-full px-6 py-3 rounded-lg font-medium text-white transition-all duration-200 ${
                      isGenerating
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-primary-600 hover:bg-primary-700'
                    }`}
                  >
                    {isGenerating ? (
                      <div className="flex items-center justify-center">
                        <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Generating Slides...
                      </div>
                    ) : (
                      'Generate Slides'
                    )}
                  </button>
                </div>
              </div>
            </div>

            <QuickStart guides={quickStartGuides} />
          </div>

          {/* Preview Section */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Preview</h2>
                {generatedSlides.length > 0 && (
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={exportToPowerPoint}
                      className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                      <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      PowerPoint
                    </button>
                    <button
                      onClick={exportToPDF}
                      className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                      <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      PDF
                    </button>
                  </div>
                )}
              </div>

              <div className="bg-gray-50 rounded-xl p-6 min-h-[600px]">
                {isGenerating ? (
                  <div className="flex flex-col items-center justify-center h-full">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
                    <p className="mt-4 text-sm text-gray-600">Creating your presentation...</p>
                  </div>
                ) : generatedSlides.length > 0 ? (
                  <div className="space-y-6">
                    <div className="flex items-center justify-between mb-4">
                      <button
                        onClick={() => setCurrentSlideIndex(Math.max(0, currentSlideIndex - 1))}
                        disabled={currentSlideIndex === 0}
                        className={`p-2 rounded-full ${
                          currentSlideIndex === 0
                            ? 'text-gray-400 cursor-not-allowed'
                            : 'text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                      </button>
                      <span className="text-sm text-gray-600">
                        Slide {currentSlideIndex + 1} of {generatedSlides.length}
                      </span>
                      <button
                        onClick={() => setCurrentSlideIndex(Math.min(generatedSlides.length - 1, currentSlideIndex + 1))}
                        disabled={currentSlideIndex === generatedSlides.length - 1}
                        className={`p-2 rounded-full ${
                          currentSlideIndex === generatedSlides.length - 1
                            ? 'text-gray-400 cursor-not-allowed'
                            : 'text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>

                    <div className="bg-white rounded-lg shadow-lg p-8 aspect-[16/9] flex flex-col">
                      <h3 className="text-2xl font-bold mb-6">
                        {generatedSlides[currentSlideIndex].title}
                      </h3>
                      <div className="flex-1">
                        <ul className="space-y-4">
                          {generatedSlides[currentSlideIndex].content.map((point, index) => (
                            <li key={index} className="flex items-start">
                              <span className="mr-2">•</span>
                              <span>{point}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      {generatedSlides[currentSlideIndex].notes && (
                        <div className="mt-4 pt-4 border-t border-gray-200">
                          <p className="text-sm text-gray-600 italic">
                            {generatedSlides[currentSlideIndex].notes}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full text-center">
                    <svg className="w-12 h-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No slides generated yet</h3>
                    <p className="mt-1 text-sm text-gray-500">Enter your content and click "Generate Slides" to get started</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <DeploymentOptions appId="powerpoint-generator" position="none" />

      <style jsx>{`
        .bg-gradient-conic {
          background-image: conic-gradient(var(--tw-gradient-stops));
        }
      `}</style>
    </div>
  );
} 